import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { loadCallOutcomes, loadWorkConfidence } from '../../../../../store/directusService';
import { updateIWSRecordAdhocCall } from '../../../../../store/participantService';
import { reverseFormatDate, todayDate } from '../../../../../utils/dateFunctions';
import { getObjFromId } from '../../../../../utils/directusFunctions';
import { hasRole } from '../../../../../utils/userRoles';
import SingleSelect from '../../../../formElements/SingleSelect';
import StaticField from '../../../../formElements/StaticField';
import TextAreaField from '../../../../formElements/TextAreaField';
import FormActions from '../../../../ui/formActions/FormActions';
import { validate } from '../validation/validateCreateIwsCall';

import app from '../../../../../app.module.css';
import form from '../../../../../commonStyles/formStyles.module.css';

const _NOTES_LIMIT = 750;

const CreateInWorkSupportCall = ({ acceptedRoles, businessName, currentCall, onClose }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        id: '',
        callOutcomeId: '',
        workConfidenceId: '',
        note: '',
        notesFromLastCall: '',
        iwsEventOwnerId: '',
        participantId: '',
        placementId: '',
        frequencyId: ''
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [callOutcomesWithoutDue, setCallOutcomesWithoutDue] = useState([]);
    const [errors, setErrors] = useState({});
    const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(false);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const callOutcomes = useSelector((state) => state.entities.directusService.callOutcomes);
    const workConfidence = useSelector((state) => state.entities.directusService.workConfidence);
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        callOutcomes?.length < 1 && dispatch(loadCallOutcomes());
        workConfidence?.length < 1 && dispatch(loadWorkConfidence());
    }, []);

    useEffect(() => {
        setNewEntry(currentCall);
    }, []);

    useEffect(() => {
        if (callOutcomes?.length < 1) return;
        setCallOutcomesWithoutDue(callOutcomes.filter((el) => el.name.toLowerCase() !== 'due'));
    }, [callOutcomes]);

    useEffect(() => {
        setIsUpdateButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === `IWS Record for ${businessName} has been updated`) {
            onFormExit();
        }
    }, [successMessage]);

    // HELPER FNS
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false } }));
    };

    const clearForm = () => {
        setNewEntry(initialState);
        setErrors({});
        setIsUpdateButtonDisabled(false);
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onFormExit = () => {
        clearForm();
        onClose();
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsUpdateButtonDisabled(true);
        const newErrors = validate(newEntry);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        dispatch(updateIWSRecordAdhocCall(newEntry, businessName));
    };

    // RENDER
    return (
        <div className={form.formWrapper}>
            <form className={form.form} onSubmit={onSubmit}>
                <h2 className={app.sectionHeading}>{`Complete IWS Call${
                    currentCall.frequencyWeekNo ? `: Week ${currentCall.frequencyWeekNo}` : ''
                }`}</h2>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id="callOutcomeId"
                            label="Call Outcome"
                            placeholder="Select call outcome..."
                            disabled={!hasRole(acceptedRoles, roles)}
                            mandatory={true}
                            menuItems={callOutcomesWithoutDue || []}
                            selectedId={newEntry.callOutcomeId}
                            selected={getObjFromId(callOutcomes, newEntry.callOutcomeId)}
                            error={errors.callOutcomeId}
                            onChange={(chosenId) => onUpdate('callOutcomeId', chosenId)}
                        />
                        <StaticField
                            id="dateRequested"
                            label="Date Of Call"
                            content={reverseFormatDate(todayDate())}
                            customClass="inputStyle"
                        />
                    </div>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id="workConfidenceId"
                            label="Work Confidence"
                            placeholder="Select work confidence..."
                            disabled={!hasRole(acceptedRoles, roles)}
                            mandatory={true}
                            customClass="standardAbsolute"
                            menuItems={workConfidence || []}
                            selectedId={newEntry.workConfidenceId}
                            selected={getObjFromId(workConfidence, newEntry.workConfidenceId)}
                            error={errors.workConfidenceId}
                            onChange={(chosenId) => onUpdate('workConfidenceId', chosenId)}
                        />
                    </div>
                </div>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <TextAreaField
                            id="note"
                            label="Notes"
                            placeholder="Enter In Work Support call notes"
                            mandatory={true}
                            disabled={!hasRole(acceptedRoles, roles)}
                            maxLength={_NOTES_LIMIT}
                            value={newEntry.note || ''}
                            count={`${newEntry.note?.length || 0}/${_NOTES_LIMIT}`}
                            error={errors.note}
                            onChange={(e) => onUpdate('note', e.target.value)}
                        />
                    </div>
                </div>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <StaticField
                            id={'notesFromLastCall'}
                            label={'Notes From Last Call'}
                            content={newEntry.notesFromLastCall || 'No notes from last call'}
                            customClass="inputStyle"
                        />
                    </div>
                </div>
                <FormActions
                    id="createIwsCallFormButton"
                    disabled={isUpdateButtonDisabled}
                    onClose={onSubmit}
                    onCancel={onFormExit}
                    btnText="Add In Work Support Call"
                />
            </form>
        </div>
    );
};

CreateInWorkSupportCall.propTypes = {
    businessName: PropTypes.string,
    currentCall: PropTypes.object,
    onClose: PropTypes.func,
    acceptedRoles: PropTypes.arrayOf(PropTypes.string)
};

export default CreateInWorkSupportCall;
